// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import { InstrumentIdentifier } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Types';
import { BurnRequest } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Burn';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useParties } from '../../../contexts/PartiesContext';
import { useRequestBurn } from '../../../hooks/mutations/registry/burn/useRequestBurn';
import TemplateContract from '../../../utils/TemplateContract';
import { getValidateAmountMessage } from '../../../utils/getValidateAmountMessage';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  holding?: TemplateContract<Holding>;
  disableDialog?: boolean;
};

export const RequestBurnDialog = ({ disableDialog, open, onClose, holding }: DialogProps) => {
  const [instrumentId, setInstrumentId] = useState<string>(holding?.payload.instrument.id || '');
  const [amount, setAmount] = useState<string>(holding?.payload.amount || '');
  const [label, setLabel] = useState<string>(holding?.payload.label || '');
  const [registrar, setRegistrar] = useState<string>(holding?.payload.registrar || '');
  const [reference, setReference] = useState<string>('');
  const { operator, party } = useParties();
  const requestBurn = useRequestBurn();
  const [burnAmountValidationMsg, setBurnAmountValidationMsg] = useState<string>('');

  const resetState = useCallback(() => {
    setInstrumentId('');
    setAmount('');
    setLabel('');
    setRegistrar('');
    setReference('');
  }, [setInstrumentId, setAmount, setLabel, setRegistrar, setReference]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);
  const getBurnValidationMsg = getValidateAmountMessage('Burn', holding?.payload.amount);
  const handleSubmit = useCallback(async () => {
    const payload: BurnRequest = {
      holdingLabel: label,
      burn: {
        operator,
        provider: party,
        registrar,
        holder: holding?.payload.owner || party,
        instrumentIdentifier: {
          source: registrar,
          id: instrumentId,
          scheme: 'RegistrarInternalScheme',
        } as InstrumentIdentifier,
        amount,
        reference,
        batch: { id: uuidv4(), size: '1', settlementFrom: null, settlementUntil: null },
      },
    };
    await requestBurn({ argument: payload });
    handleClose();
  }, [
    label,
    amount,
    instrumentId,
    registrar,
    reference,
    requestBurn,
    handleClose,
    operator,
    party,
    holding?.payload.owner,
  ]);

  const disableSubmit = !!burnAmountValidationMsg.length || !instrumentId || !amount || !registrar;
  return (
    <FormDialog
      disableDialog={disableDialog}
      open={open}
      title="Request Burn"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput
        label="Instrument"
        value={instrumentId}
        setValue={setInstrumentId}
        disabled={!!holding}
        required
      />
      <TextInput
        type="number"
        validationMessage={burnAmountValidationMsg}
        setValidationMessage={setBurnAmountValidationMsg}
        getValidationMessage={getBurnValidationMsg}
        label="Amount"
        value={amount}
        setValue={setAmount}
        required
      />
      <TextInput label="Label" value={label} setValue={setLabel} disabled={!!holding} hidden />
      <TextInput
        label="Registrar"
        value={registrar}
        setValue={setRegistrar}
        required
        disabled={!!holding}
      />
      <TextInput label="Reference" value={reference} setValue={setReference} />
    </FormDialog>
  );
};
